import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import CompanySelect from '../../FashionOrders/SelectItem/CompanySelect';
import UserSelect from '../../Equipment/Select/UserSelect';
import SelectAndSearch from '../../../../components/SelectAndSearch';
import {
  FINANCIAL_ENTRY_CATEGORY_OPTIONS,
  FINANCIAL_ENTRY_STATUS_OPTIONS,
  FINANCIAL_ENTRY_SUBCATEGORY_OPTIONS,
  FINANCIAL_ENTRY_TYPE,
  FINANCIAL_ENTRY_TYPE_OPTIONS,
  getSubcategoryLabel,
} from '../constants';
import FormOptionSelect from './FormOptionSelect';
import { FinancialEntryFormSchemaType } from './schema';
import {
  CardTitle,
  DropdownHeaderSelectedItems,
  DropdownSelectedItemTag,
  FormGrid,
  FormSearchableSelectWrap,
  StyledCard,
  StyledFormGroup,
} from './style';

const GATEWAY = process.env.REACT_APP_GATEWAY_BASE_URL;
const COMPANIES_API = `${GATEWAY}/companies/companies`;
const USERS_API = `${GATEWAY}/backend/user/findAll`;

interface CompanyOption {
  id: number;
  companyName: string;
}

interface UserOption {
  id: number;
  name: string;
}

const SUBCATEGORY_SEARCH_OPTIONS = FINANCIAL_ENTRY_SUBCATEGORY_OPTIONS.map(
  (option) => ({
    id: option.value,
    name: option.label,
  }),
);

const GeneralInfoSection: React.FC = () => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FinancialEntryFormSchemaType>();
  const [userSelectKey, setUserSelectKey] = useState(0);

  const companyId = watch('companyId');
  const companyName = watch('companyName');
  const linkedUsers = watch('linkedUsers') ?? [];
  const subcategory = watch('subcategory');
  const subcategoryName =
    watch('subcategoryName') || getSubcategoryLabel(subcategory);
  const installments = watch('installments') ?? [];

  const handleAddLinkedUser = (item: UserOption | null) => {
    if (!item?.id) return;
    if (linkedUsers.some((user) => user.id === item.id)) {
      setUserSelectKey((key) => key + 1);
      return;
    }
    setValue(
      'linkedUsers',
      [...linkedUsers, { id: item.id, name: item.name }],
      { shouldValidate: true },
    );
    setUserSelectKey((key) => key + 1);
  };

  const handleRemoveLinkedUser = (userId: number) => {
    setValue(
      'linkedUsers',
      linkedUsers.filter((user) => user.id !== userId),
      { shouldValidate: true },
    );
  };

  const handleTypeChange = (value: number | null) => {
    setValue('type', value, { shouldValidate: true });
    if (value === FINANCIAL_ENTRY_TYPE.EXPENSE) {
      installments.forEach((_, index) => {
        setValue(`installments.${index}.hasCommission`, false, {
          shouldDirty: true,
        });
        setValue(`installments.${index}.commission`, '', { shouldDirty: true });
      });
    }
  };

  return (
    <StyledCard>
      <CardTitle>Geral</CardTitle>
      <FormGrid cols={3}>
        <div>
          <Controller
            name="companyId"
            control={control}
            render={() => (
              <FormSearchableSelectWrap $hasError={!!errors.companyId}>
                <CompanySelect<CompanyOption>
                  label="Empresa"
                  apiUrl={COMPANIES_API}
                  displayField="companyName"
                  showDocument={false}
                  value={{ id: companyId ?? 0, companyName: companyName ?? '' }}
                  onChange={(item) => {
                    setValue('companyId', item.id > 0 ? item.id : null, {
                      shouldValidate: true,
                    });
                    setValue('companyName', item.companyName ?? '');
                  }}
                />
              </FormSearchableSelectWrap>
            )}
          />
        </div>

        <div>
          <Controller
            name="linkedUsers"
            control={control}
            render={() => (
              <FormSearchableSelectWrap $hasError={!!errors.linkedUsers}>
                <UserSelect<UserOption>
                  key={userSelectKey}
                  label="Usuário vinculado"
                  apiUrl={USERS_API}
                  displayField="name"
                  value={null}
                  onChange={handleAddLinkedUser}
                />
                <DropdownHeaderSelectedItems>
                  {linkedUsers.map((user) => (
                    <DropdownSelectedItemTag key={user.id}>
                      {user.name}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveLinkedUser(user.id);
                        }}
                      >
                        x
                      </button>
                    </DropdownSelectedItemTag>
                  ))}
                </DropdownHeaderSelectedItems>
              </FormSearchableSelectWrap>
            )}
          />
        </div>

        <div>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <FormOptionSelect
                label="Tipo"
                value={field.value ?? null}
                onChange={handleTypeChange}
                options={FINANCIAL_ENTRY_TYPE_OPTIONS}
                hasError={!!errors.type}
              />
            )}
          />
        </div>

        <div>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <FormOptionSelect
                label="Categoria"
                value={field.value ?? null}
                onChange={field.onChange}
                options={FINANCIAL_ENTRY_CATEGORY_OPTIONS}
                hasError={!!errors.category}
              />
            )}
          />
        </div>

        <div>
          <Controller
            name="subcategory"
            control={control}
            render={() => (
              <FormSearchableSelectWrap $hasError={!!errors.subcategory}>
                <SelectAndSearch
                  label="Subcategoria"
                  options={SUBCATEGORY_SEARCH_OPTIONS}
                  selectedOption={
                    subcategory != null
                      ? { id: subcategory, name: subcategoryName || '' }
                      : null
                  }
                  onSelect={(option) => {
                    setValue('subcategory', option.id ?? null, {
                      shouldValidate: true,
                    });
                    setValue('subcategoryName', option.name ?? '');
                  }}
                  onClear={() => {
                    setValue('subcategory', null, { shouldValidate: true });
                    setValue('subcategoryName', '');
                  }}
                  placeholder="Selecione"
                  minChars={0}
                />
              </FormSearchableSelectWrap>
            )}
          />
        </div>

        <div>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <FormOptionSelect
                label="Status"
                value={field.value ?? null}
                onChange={field.onChange}
                options={FINANCIAL_ENTRY_STATUS_OPTIONS}
                allowEmpty={false}
                hasError={!!errors.status}
              />
            )}
          />
        </div>

        <StyledFormGroup>
          <label>Descrição</label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <textarea {...field} placeholder="Descrição do lançamento" />
            )}
          />
        </StyledFormGroup>
      </FormGrid>
    </StyledCard>
  );
};

export default GeneralInfoSection;
